import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { Paciente, StatusTransplante } from "../../../types";
import { useAuth } from "../../auth/context/AuthContext";
import { db } from "../../../lib/firebase";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  arrayUnion,
  onSnapshot,
  QueryDocumentSnapshot,
  DocumentData,
} from "firebase/firestore";

import { enderecoVigente, planoDoBloco, blocoTemEndereco } from "../../../shared/utils/enderecoPaciente";
// ============================================
// 🔁 TRADUTOR ÚNICO: doc Firestore → Paciente (B204, AI_94)
// Antes duplicado (~85 linhas idênticas) em carregarPacientes()
// e no listener onSnapshot — achado A3 da auditoria AI_92
// (mudança num lado e não no outro = bug sutil). Qualquer campo
// novo do Paciente entra SÓ aqui.
// ============================================
function mapearDocPaciente(
  docSnap: QueryDocumentSnapshot<DocumentData>
): Paciente {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    // Dados Pessoais
    nome: data.nome,
    cpf: data.cpf || "",
    rg: data.rg,
    cns: data.cns,
    rgct: data.rgct,
    hospub: data.hospub,
    dataNascimento: data.dataNascimento,
    sexo: data.sexo as "M" | "F",
    nomeMae: data.nomeMae,
    estadoCivil: data.estadoCivil,
    profissao: data.profissao,
    tipoSanguineo: data.tipoSanguineo || data.grupoSanguineo,
    grupoSanguineo: data.tipoSanguineo || data.grupoSanguineo,

    // Contatos
    telefone: data.telefone || data.telefone1,
    telefone1: data.telefone1,
    telefone2: data.telefone2,
    telefone3: data.telefone3,
    email: data.email,

    // Contato de Emergência
    contatoEmergenciaNome:
      data.contatoEmergenciaNome || data.contatoEmergencia?.nome,
    contatoEmergenciaTelefone:
      data.contatoEmergenciaTelefone || data.contatoEmergencia?.telefone,
    contatoEmergenciaRelacao:
      data.contatoEmergenciaRelacao || data.contatoEmergencia?.parentesco,
    contatoEmergencia: data.contatoEmergencia,

    // Endereço — B394 (AI_158): o BLOCO `endereco` manda; os campos soltos
    // (logradouro/numero/...) só entram quando o bloco está vazio. A ordem
    // antiga (`data.logradouro || data.endereco?.rua`) fossilizava o endereço
    // velho: a tela de edição só escreve o bloco, e a leitura + todos os
    // documentos impressos liam os soltos. Fonte única: enderecoPaciente.ts.
    ...enderecoVigente(data as any),
    endereco: data.endereco,

    // Dados Clínicos
    peso: data.peso,
    altura: data.altura,
    alergias: data.alergias,
    medicacoesEmUso: data.medicacoesEmUso,
    comorbidades: data.comorbidades,

    // Tipo de Atendimento
    tipoAtendimento: data.tipoAtendimento || "ambulatorial",
    interfaceAtual: data.interfaceAtual || "ambulatorial",
    statusTransplante: data.statusTransplante as StatusTransplante,

    // Dados de Transplante
    equipeMedica: data.equipeMedica, // B242 (AI_112) — médico/equipe responsável (Busca Avançada)
    inscricaoTx: data.inscricaoTx || undefined,
    // Dados do Transplante (cirurgia)
    dadosTransplante: data.dadosTransplante || undefined,
    transplantes: data.transplantes || undefined,
    // Avaliação Clínica
    avaliacaoClinica: data.avaliacaoClinica || undefined,
    // B351 (AI_152) — sem esta linha o campo é GRAVADO e some na leitura seguinte
    // (armadilha já registrada na AI_141 com as análises setoriais).
    avaliacaoDoador: data.avaliacaoDoador || undefined,
    scores: data.scores || undefined,
    // 🧬 B312-FU/AI_141 — última análise de marcadores (senão o campo é
    // descartado na leitura e o botão "Ver última análise" nunca aparece).
    ultimaAnaliseMarcadores: data.ultimaAnaliseMarcadores || undefined,
    // 🧫 B313/AI_141 — última análise de sorologias (mesmo motivo).
    ultimaAnaliseSorologias: data.ultimaAnaliseSorologias || undefined,
    // 🤖 B314/AI_141 — última análise de consulta/evolução.
    ultimaAnaliseEvolucao: data.ultimaAnaliseEvolucao || undefined,
    // 🧪 B323-FU/AI_145 — última análise de exames laboratoriais (senão o campo
    // é descartado na leitura e o botão "Ver última análise" nunca aparece).
    ultimaAnaliseLab: data.ultimaAnaliseLab || undefined,
    numeroRGCT: data.numeroRGCT || data.inscricaoTx?.numeroRGCT || data.rgct,
    dataInscricao: data.dataInscricao,
    situacaoEspecial: data.situacaoEspecial,
    dataAprovacaoSE: data.dataAprovacaoSE,
    tipoSE: data.tipoSE,
    dataTransplante: data.dataTransplante,
    hospitalTransplante: data.hospitalTransplante,

    // Metadados
    dataCadastro:
      data.criadoEm?.toDate?.()?.toISOString?.() || new Date().toISOString(),
    cadastradoPor: data.cadastradoPor,
    cadastradoPorNome: data.cadastradoPorNome,
    // Status do Paciente no Serviço
    statusPaciente: data.statusPaciente || "ativo",
    dataStatusPaciente: data.dataStatusPaciente || null,
    motivoStatus: data.motivoStatus || null,
    ativo: data.ativo ?? true,
    // B110 — log de alterações (Auditoria → sub-aba "Log de Alterações")
    historicoEdicoes: data.historicoEdicoes || [],
  } as Paciente;
}

// B110 — campos de metadados que NÃO contam como "campo alterado" no log.
const CAMPOS_META_EDICAO = new Set([
  "editadoPor",
  "editadoPorNome",
  "atualizadoEm",
  "historicoEdicoes",
]);

// B81/fcd1694 (AI_99) estendido p/ PACIENTES (AI_115): campos que NUNCA devem
// ir crus no payload de update. As telas de edição reenviam o
// paciente INTEIRO, e `dataCadastro` vem CONVERTIDO (Timestamp→string ISO em
// mapearDocPaciente). A regra do firestore.rules exige
// imutavel('cadastradoPor'/'cadastradoPorNome'/'dataCadastro') → o valor
// convertido bate como "diferente" do Timestamp gravado e a regra NEGAVA o
// update inteiro ("Missing or insufficient permissions"). Saneamos o payload:
// imutáveis + id/criadoEm + metadados (que o app reescreve explicitamente).
const CAMPOS_BLOQUEADOS_UPDATE = new Set<string>([
  "id",
  "criadoEm",
  "cadastradoPor",
  "cadastradoPorNome",
  "dataCadastro",
  ...Array.from(CAMPOS_META_EDICAO),
]);

interface PacientesContextProps {
  pacientes: Paciente[];
  pacienteSelecionado: Paciente | null;
  loading: boolean;
  adicionarPaciente: (
    dados: Omit<Paciente, "id" | "dataCadastro">
  ) => Promise<void>;
  selecionarPaciente: (paciente: Paciente) => void;
  desselecionarPaciente: () => void;
  atualizarPaciente: (id: string, dados: Partial<Paciente>) => Promise<void>;
  removerPaciente: (id: string) => Promise<void>;
  carregarPacientes: () => Promise<void>;
}

const PacientesContext = createContext<PacientesContextProps | undefined>(
  undefined
);

export function PacientesProvider({ children }: { children: ReactNode }) {
  const { usuarioLogado } = useAuth();

  const [pacientes, setPacientes] = useState<Paciente[]>([]);
  const [pacienteSelecionado, setPacienteSelecionado] =
    useState<Paciente | null>(null);
  const [loading, setLoading] = useState(true);

  // ============================================
  // 📥 CARREGAR PACIENTES DO FIREBASE
  // ============================================
  const carregarPacientes = async () => {
    try {
      setLoading(true);
      console.log("🔍 Carregando pacientes do Firebase...");

      const q = query(
        collection(db, "pacientes"),
        where("ativo", "==", true),
        orderBy("criadoEm", "desc")
      );

      const querySnapshot = await getDocs(q);

      // Tradutor único (B204) — ver mapearDocPaciente no topo do arquivo
      const pacientesFormatados: Paciente[] =
        querySnapshot.docs.map(mapearDocPaciente);

      setPacientes(pacientesFormatados);
      console.log("✅ Pacientes carregados:", pacientesFormatados.length);
    } catch (error) {
      console.error("❌ Erro ao carregar pacientes:", error);
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // 🔄 CARREGAR AO MONTAR E QUANDO LOGAR
  // ============================================
  useEffect(() => {
    if (!usuarioLogado) {
      setLoading(false);
      return;
    }

    setLoading(true);
    console.log("🔍 Escutando pacientes em tempo real...");

    const q = query(
      collection(db, "pacientes"),
      where("ativo", "==", true),
      orderBy("criadoEm", "desc")
    );

    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        // Tradutor único (B204) — ver mapearDocPaciente no topo do arquivo
        const pacientesFormatados: Paciente[] =
          querySnapshot.docs.map(mapearDocPaciente);

        setPacientes(pacientesFormatados);
        setLoading(false);
        console.log(
          "✅ Pacientes atualizados (tempo real):",
          pacientesFormatados.length
        );

        // Atualizar pacienteSelecionado se existir na lista
        setPacienteSelecionado((prev) => {
          if (!prev) return null;
          const atualizado = pacientesFormatados.find((p) => p.id === prev.id);
          return atualizado || prev;
        });
      },
      (error) => {
        console.error("❌ Erro no listener de pacientes:", error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [usuarioLogado]);

  // ============================================
  // ➕ ADICIONAR PACIENTE
  // ============================================
  const adicionarPaciente = async (
    dados: Omit<Paciente, "id" | "dataCadastro">
  ) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      // B318 (AI_143, achado M5 da auditoria Codex): sem PII no console —
      // nome do paciente não vai a log (LGPD, espelha o B196 do backend).
      console.log("📝 Adicionando paciente...");

      await addDoc(collection(db, "pacientes"), {
        // Dados Pessoais
        nome: dados.nome,
        cpf: dados.cpf || null,
        rg: dados.rg || null,
        cns: dados.cns || null,
        rgct: dados.rgct || null,
        hospub: dados.hospub || null,
        dataNascimento: dados.dataNascimento,
        sexo: dados.sexo,
        nomeMae: dados.nomeMae || null,
        estadoCivil: dados.estadoCivil || null,
        profissao: dados.profissao || null,
        tipoSanguineo: dados.tipoSanguineo || dados.grupoSanguineo || null,
        grupoSanguineo: dados.tipoSanguineo || dados.grupoSanguineo || null,

        // Contatos
        telefone: dados.telefone || null,
        // 🔴 B345-FU (AI_150) — a ORDEM aqui era `dados.telefone1 || dados.telefone`,
        // e essa inversão fossilizava o número: os formulários só escrevem
        // `telefone`, então `dados.telefone1` chegava com o valor ANTIGO (vindo do
        // carregamento) e vencia — o cadastro era editado e o `telefone1` nunca
        // mudava. Como o envio ao paciente e o 2º fator liam `telefone1`, a receita
        // seguia indo para o número velho para sempre. Os dois campos agora recebem
        // o MESMO valor: o que o médico acabou de digitar.
        telefone1: dados.telefone || dados.telefone1 || null,
        telefone2: dados.telefone2 || null,
        telefone3: dados.telefone3 || null,
        email: dados.email || null,

        // Contato de Emergência (campos separados E objeto para compatibilidade)
        contatoEmergenciaNome:
          dados.contatoEmergenciaNome || dados.contatoEmergencia?.nome || null,
        contatoEmergenciaTelefone:
          dados.contatoEmergenciaTelefone ||
          dados.contatoEmergencia?.telefone ||
          null,
        contatoEmergenciaRelacao:
          dados.contatoEmergenciaRelacao ||
          dados.contatoEmergencia?.parentesco ||
          null,
        contatoEmergencia: dados.contatoEmergencia || null,

        // Endereço (campos separados E objeto para compatibilidade)
        cep: dados.cep || dados.endereco?.cep || null,
        logradouro: dados.logradouro || dados.endereco?.rua || null,
        numero: dados.numero || dados.endereco?.numero || null,
        complemento: dados.complemento || dados.endereco?.complemento || null,
        bairro: dados.bairro || dados.endereco?.bairro || null,
        cidade: dados.cidade || dados.endereco?.cidade || null,
        estado: dados.estado || dados.endereco?.estado || null,
        endereco: dados.endereco || null,

        // Dados Clínicos
        peso: dados.peso || null,
        altura: dados.altura || null,
        alergias: dados.alergias || null,
        medicacoesEmUso: dados.medicacoesEmUso || null,
        comorbidades: dados.comorbidades || null,

        // Tipo de Atendimento
        tipoAtendimento: dados.tipoAtendimento || "ambulatorial",
        interfaceAtual: dados.interfaceAtual || "ambulatorial",
        statusTransplante: dados.statusTransplante || null,

        // Dados de Transplante (campos legados)
        dataInscricao: dados.dataInscricao || null,
        situacaoEspecial: dados.situacaoEspecial || false,
        dataAprovacaoSE: dados.dataAprovacaoSE || null,
        tipoSE: dados.tipoSE || null,
        dataTransplante: dados.dataTransplante || null,
        hospitalTransplante: dados.hospitalTransplante || null,

        // Dados de Inscrição (novo formato)
        inscricaoTx: dados.inscricaoTx
          ? {
              statusInscricao: dados.inscricaoTx.statusInscricao || null,
              dataInscricao: dados.inscricaoTx.dataInscricao || null,
              numeroRGCT: dados.inscricaoTx.numeroRGCT || null,
              // MELD-Na vigente da inscrição (B218, AI_101) — TRANSITÓRIO: sai
              // quando a Portaria 8.041/2025 entrar em vigor (MELD 3.0 oficial).
              meldNa:
                dados.inscricaoTx.meldNa !== undefined &&
                dados.inscricaoTx.meldNa !== ""
                  ? Number(dados.inscricaoTx.meldNa)
                  : null,
              situacaoEspecial: dados.inscricaoTx.situacaoEspecial || false,
              dataAprovacaoSE: dados.inscricaoTx.dataAprovacaoSE || null,
              tipoSE: dados.inscricaoTx.tipoSE || null,
              dataTransplante: dados.inscricaoTx.dataTransplante || null,
              hospitalTransplante:
                dados.inscricaoTx.hospitalTransplante || null,
            }
          : null,

        // Status do Paciente no Serviço
        statusPaciente: dados.statusPaciente || "ativo",
        dataStatusPaciente: dados.dataStatusPaciente || null,
        motivoStatus: dados.motivoStatus || null,

        // Metadados
        cadastradoPor: usuarioLogado.id,
        cadastradoPorNome: usuarioLogado.nome,
        ativo: true,
        // B110 — primeira entrada do log de alterações (cadastro)
        historicoEdicoes: [
          {
            data: new Date().toISOString(),
            usuarioId: usuarioLogado.id,
            usuarioNome: usuarioLogado.nome,
            acao: "cadastro",
          },
        ],
        criadoEm: serverTimestamp(),
        atualizadoEm: serverTimestamp(),
      });

      console.log("✅ Paciente adicionado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao adicionar paciente:", error);
      throw error;
    }
  };

  // ============================================
  // 👁️ SELECIONAR PACIENTE
  // ============================================
  const selecionarPaciente = (paciente: Paciente) => {
    setPacienteSelecionado(paciente);
    // B318 (AI_143): id no lugar do nome — sem PII no console (LGPD).
    console.log("✅ Paciente selecionado:", paciente.id);
  };

  // ============================================
  // ❌ DESSELECIONAR PACIENTE
  // ============================================
  const desselecionarPaciente = () => {
    setPacienteSelecionado(null);
    console.log("✅ Paciente desselecionado");
  };

  // ============================================
  // ✏️ ATUALIZAR PACIENTE
  // ============================================
  const atualizarPaciente = async (id: string, dados: Partial<Paciente>) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("📝 Atualizando paciente:", id);

      const dadosFirebase: any = {
        editadoPor: usuarioLogado.id,
        editadoPorNome: usuarioLogado.nome,
        atualizadoEm: serverTimestamp(),
      };

      // Remove undefined recursivamente para compatibilidade com Firebase
      const limparUndefined = (obj: any): any => {
        if (obj === null || obj === undefined) return null;
        if (typeof obj !== "object") return obj;
        if (Array.isArray(obj)) return obj.map(limparUndefined);
        const resultado: any = {};
        Object.keys(obj).forEach((k) => {
          const v = obj[k];
          if (v !== undefined) {
            resultado[k] = limparUndefined(v);
          }
        });
        return resultado;
      };

      // Adiciona apenas campos que foram alterados, PULANDO os bloqueados
      // (imutáveis por regra + metadados que o app reescreve) — senão o
      // `dataCadastro` convertido nega o update inteiro (B81/AI_115).
      Object.keys(dados).forEach((key) => {
        const value = (dados as any)[key];
        if (value !== undefined && !CAMPOS_BLOQUEADOS_UPDATE.has(key)) {
          dadosFirebase[key] = limparUndefined(value);
        }
      });

      // 🔴 B345-FU3 (AI_150) — MANTÉM `telefone1` EM SINCRONIA COM `telefone`.
      //
      // As telas de edição mandam o paciente INTEIRO espalhado, então o
      // `telefone1` (campo legado) voltava com o valor ANTIGO e era regravado —
      // o banco ficava com dois números diferentes para o mesmo paciente.
      // Hoje isso não quebra o envio (todos os leitores usam telefoneVigente(),
      // que prefere `telefone`), mas é uma mina: qualquer código, exportação ou
      // relatório futuro que leia `telefone1` pegaria o número velho. Espelhar
      // aqui repara cada paciente na primeira edição, sem migração.
      //
      // Só age quando `telefone` VEIO no payload: update parcial que não toca
      // telefone nenhum continua sem mexer em nada.
      if (Object.prototype.hasOwnProperty.call(dados, "telefone")) {
        const tel = (dados as any).telefone;
        if (tel !== undefined) dadosFirebase.telefone1 = limparUndefined(tel);
      }

      // 🔴 B394 (AI_158) — MANTÉM OS CAMPOS SOLTOS DE ENDEREÇO EM SINCRONIA
      // COM O BLOCO `endereco`. Mesma mina do telefone1: a tela de edição só
      // escreve o bloco, mas manda o paciente inteiro, e os soltos
      // (logradouro/numero/bairro/cidade/estado/cep) voltavam com o valor
      // ANTIGO — e são eles que o receituário, o pedido, a RCE e as fichas do
      // SUS imprimem. Espelhar aqui repara cada paciente na primeira edição.
      // Só age quando o bloco VEIO com conteúdo: update parcial sem endereço
      // (ou bloco vazio) não mexe em nada.
      if (blocoTemEndereco((dados as any).endereco)) {
        const plano = planoDoBloco((dados as any).endereco);
        (Object.keys(plano) as (keyof typeof plano)[]).forEach((k) => {
          dadosFirebase[k] = plano[k] ?? null;
        });
      }

      // B110 — anexa uma entrada ao log de alterações (Auditoria). Usa
      // arrayUnion p/ não sobrescrever o histórico; timestamp do cliente
      // (serverTimestamp não é permitido dentro de elementos de array).
      // Fix AI_98 (achado de Leonardo): "campos alterados" compara o VALOR
      // de cada chave contra o doc atual em memória — as telas de edição
      // enviam o paciente inteiro e o log saía com todos os campos.
      const atual: any = pacientes.find((p) => p.id === id) || {};
      const serializar = (v: any) => JSON.stringify(limparUndefined(v ?? null));
      const camposAlterados = Object.keys(dados).filter(
        (k) =>
          !CAMPOS_BLOQUEADOS_UPDATE.has(k) &&
          serializar((dados as any)[k]) !== serializar(atual[k])
      );
      if (camposAlterados.length > 0) {
        dadosFirebase.historicoEdicoes = arrayUnion({
          data: new Date().toISOString(),
          usuarioId: usuarioLogado.id,
          usuarioNome: usuarioLogado.nome,
          acao: "edicao",
          camposAlterados,
        });
      }

      await updateDoc(doc(db, "pacientes", id), dadosFirebase);

      console.log("✅ Paciente atualizado com sucesso!");
    } catch (error) {
      console.error("❌ Erro ao atualizar paciente:", error);
      throw error;
    }
  };

  // ============================================
  // 🗑️ REMOVER PACIENTE (SOFT DELETE)
  // ============================================
  const removerPaciente = async (id: string) => {
    if (!usuarioLogado) {
      console.error("❌ Usuário não está logado");
      throw new Error("Usuário não está logado");
    }

    try {
      console.log("🗑️ Removendo paciente:", id);

      await updateDoc(doc(db, "pacientes", id), {
        ativo: false,
        editadoPor: usuarioLogado.id,
        editadoPorNome: usuarioLogado.nome,
        atualizadoEm: serverTimestamp(),
      });

      console.log("✅ Paciente removido com sucesso!");

      if (pacienteSelecionado?.id === id) {
        desselecionarPaciente();
      }
    } catch (error) {
      console.error("❌ Erro ao remover paciente:", error);
      throw error;
    }
  };

  return (
    <PacientesContext.Provider
      value={{
        pacientes,
        pacienteSelecionado,
        loading,
        adicionarPaciente,
        selecionarPaciente,
        desselecionarPaciente,
        atualizarPaciente,
        removerPaciente,
        carregarPacientes,
      }}
    >
      {children}
    </PacientesContext.Provider>
  );
}

export function usePacientes() {
  const context = useContext(PacientesContext);
  if (!context) {
    throw new Error("usePacientes deve ser usado dentro de PacientesProvider");
  }
  return context;
}
